import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import { useAtom } from 'jotai';
import { openLogRequestModalAtom } from '../components/create-log-request-modal/CreateLogRequestModal.atom';
import { CreateLogRequestModal } from '../components/create-log-request-modal/CreateLogRequestModal.ctrl';
import type { CreateLogRequestFormularySchema } from '../components/create-log-request-modal/hooks/useFormularyOfCreateLogRequest.hook';

export interface UseLogRequestsReturn {
  readonly createLogRequestComponent: ReactNode;
  readonly handleCreateLogRequestDialog: () => void;
}

export interface UseLogRequestsProps {
  readonly logicalDeviceId: string;
  readonly onSubmission?: () => void;
}

export function useLogRequests({ logicalDeviceId, onSubmission }: UseLogRequestsProps): UseLogRequestsReturn {
  const [isModalOpen, setIsModalOpen] = useAtom(openLogRequestModalAtom);

  const handleCreateLogRequestDialog = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  const onCancel = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const onCreateLogRequest = useCallback(
    (_: CreateLogRequestFormularySchema) => {
      onCancel();
      onSubmission?.();
    },
    [onCancel, onSubmission],
  );

  const createLogRequestComponent = useMemo(
    () => (
      <CreateLogRequestModal
        isOpen={isModalOpen}
        onSubmit={onCreateLogRequest}
        onCancel={onCancel}
        logicalDeviceId={logicalDeviceId}
      />
    ),
    [isModalOpen, onCancel, onCreateLogRequest, logicalDeviceId],
  );

  return {
    createLogRequestComponent,
    handleCreateLogRequestDialog,
  };
}
